import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEventStatus, sortEvents } from "../lib/eventsUtils";
import { useEventsData } from "../contexts/EventsContext";
import EventCard from "./EventCard";
import EventDetailsModal from "./EventDetailsModal";
import { ChevronDown, ChevronUp, Layers, Loader2 } from "lucide-react";

const TABS = [
  { id: "upcoming", label: "القادمة" },
  { id: "live", label: "المتوفرة الآن" },
  { id: "ended", label: "المنتهية" },
  { id: "all", label: "الكل" },
];

const INITIAL_VISIBLE = 3;

const EventsSection = () => {
  const [tab, setTab] = useState("upcoming");
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const { events, loading, error } = useEventsData();

  const counts = useMemo(() => {
    const c = { all: events.length, live: 0, upcoming: 0, ended: 0 };
    events.forEach((e) => {
      c[getEventStatus(e)] += 1;
    });
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    const sorted = sortEvents(events);
    if (tab === "all") return sorted;
    return sorted.filter((e) => getEventStatus(e) === tab);
  }, [events, tab]);

  const visible = useMemo(
    () => (showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE)),
    [filtered, showAll]
  );
  const hasMore = filtered.length > INITIAL_VISIBLE;

  const handleTabChange = (id) => {
    setTab(id);
    setShowAll(false); // reset back to first 3 when switching tab
  };

  const handleCalculate = (event) => {
    // العجلات المزدوجة تُوجَّه إلى الصفحات المتخصّصة بدل الحاسبة الأساسية.
    if (event?.calculatorType === "dual-weapons") {
      navigate("/dual-weapons", {
        state: { fromEvent: event.id, eventName: event.name },
      });
      return;
    }
    if (event?.calculatorType === "dual-characters") {
      navigate("/dual-characters", {
        state: { fromEvent: event.id, eventName: event.name },
      });
      return;
    }
    // العجلات الأخرى: نُحدّد فئة العجلة في الحاسبة الأساسية ونمرّر إليها.
    if (event?.wheelId) {
      window.dispatchEvent(
        new CustomEvent("rush:setCalculatorWheel", {
          detail: { wheelId: event.wheelId },
        })
      );
    }
    const el = document.getElementById("calculator");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.hash = "calculator";
    }
  };

  const handleOpenDetails = (event) => {
    setSelected(event);
    setOpen(true);
  };

  return (
    <section id="events" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C9A96A]/[0.06] border border-[#C9A96A]/20 text-[#D4B98A] text-[11px] font-medium mb-3">
            <Layers className="w-3 h-3" />
            <span>العجلات والأحداث</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight">
            جدول العجلات القادمة والحالية
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            تابع مواعيد العجلات واحسب تكلفتها مباشرة
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-6 -mx-1 px-1">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`events-tab-${t.id}`}
              onClick={() => handleTabChange(t.id)}
              className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors border ${
                active
                  ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
                  : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
              }`}
            >
              {t.label}
              <span
                className={`ms-1.5 px-1.5 py-0.5 rounded-md text-[10px] ${
                  active
                    ? "bg-[#0A1128]/50 text-[#E5CB94]"
                    : "bg-white/[0.04] text-slate-400"
                }`}
              >
                {counts[t.id]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading && events.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-4 h-4 animate-spin" />
          جارٍ تحميل العجلات...
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-center py-8 text-sm">
          تعذّر تحميل العجلات. أعد تحميل الصفحة.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm">
          لا توجد عجلات في هذا القسم حالياً.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="events-grid">
            {visible.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onCalculate={handleCalculate}
                onOpenDetails={handleOpenDetails}
              />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                data-testid="events-show-more"
                onClick={() => setShowAll((v) => !v)}
                className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-[#C9A96A]/10 hover:bg-[#C9A96A]/20 border border-[#C9A96A]/30 hover:border-[#C9A96A]/50 text-[#E5CB94] text-sm font-bold transition-colors"
              >
                {showAll ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    إظهار أقل
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    إظهار المزيد ({filtered.length - INITIAL_VISIBLE})
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      <EventDetailsModal
        event={selected}
        open={open}
        onOpenChange={setOpen}
        onCalculate={handleCalculate}
      />
    </section>
  );
};

export default EventsSection;
